'use client';

import React, { useMemo, useRef, useEffect, useState } from 'react';
import { startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isToday, format, differenceInMinutes, startOfDay, endOfDay } from 'date-fns';
import { Plus } from 'lucide-react';
import { cn } from '@/app/components/lib/utils';
import { CALENDAR_COLOR_MAP } from './constants';
const SLOT_HEIGHT = 48; // 48px per 30 minutes (96px per hour)
const HOURS = Array.from({
  length: 24
}, (_, i) => i);
export const CalendarWeekView = ({
  currentDate,
  selectedDate,
  events = [],
  onDateSelect,
  onEventClick,
  onAddEvent,
  className,
  slotProps
}) => {
  const containerRef = useRef(null);
  const [now, setNow] = useState(new Date());

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // 7 days of the active week (Mon - Sun)
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, {
      weekStartsOn: 1
    });
    const end = endOfWeek(currentDate, {
      weekStartsOn: 1
    });
    return eachDayOfInterval({
      start,
      end
    });
  }, [currentDate]);

  // Normalize events
  const parsedEvents = useMemo(() => {
    return events.map(ev => ({
      ...ev,
      startDate: typeof ev.start === 'string' ? new Date(ev.start) : ev.start,
      endDate: typeof ev.end === 'string' ? new Date(ev.end) : ev.end
    }));
  }, [events]);

  // Auto-scroll to earliest event of the week or 8 AM (768px)
  useEffect(() => {
    if (!containerRef.current) return;
    const weekStart = startOfDay(weekDays[0]);
    const weekEnd = endOfDay(weekDays[6]);
    const weekEvents = parsedEvents.filter(ev => ev.startDate >= weekStart && ev.startDate <= weekEnd);
    let earliestMinutes = 8 * 60; // 8:00 AM default
    if (weekEvents.length > 0) {
      const eventMinutes = weekEvents.map(ev => ev.startDate.getHours() * 60 + ev.startDate.getMinutes());
      earliestMinutes = Math.min(...eventMinutes);
    }
    const scrollTarget = Math.max(0, earliestMinutes / 30 * SLOT_HEIGHT - SLOT_HEIGHT);
    containerRef.current.scrollTop = scrollTarget;
  }, [weekDays, parsedEvents]);

  // Current time position calculation
  const currentMinutesFromMidnight = now.getHours() * 60 + now.getMinutes();
  const currentTimeTop = currentMinutesFromMidnight / 30 * SLOT_HEIGHT;
  const weekSlot = slotProps?.weekView;
  return <div {...weekSlot?.root} className={cn('flex flex-1 flex-col overflow-hidden bg-white dark:bg-zinc-900', className, weekSlot?.root?.className)}>
            {/* Sticky 7-Day Header */}
            <div {...weekSlot?.headerRow} className={cn('sticky top-0 z-20 flex border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-2xs', weekSlot?.headerRow?.className)}>
                {/* Left spacer for time column */}
                <div className="w-16 sm:w-20 shrink-0 border-r border-zinc-200 dark:border-zinc-800" />

                {/* 7 Columns for weekdays */}
                <div className="grid flex-1 grid-cols-7">
                    {weekDays.map((day, idx) => {
          const isCurrentDay = isToday(day);
          const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
          const isLast = idx === weekDays.length - 1;
          return <button key={day.toISOString()} type="button" onClick={() => onDateSelect?.(day)} className={cn('flex flex-col items-center justify-center gap-1 py-3 text-center transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-850 cursor-pointer', !isLast && 'border-r border-zinc-200 dark:border-zinc-800')}>
                                <span className="text-xs font-semibold uppercase text-zinc-500 dark:text-zinc-400">{format(day, 'EEE')}</span>
                                <span className={cn('flex size-7 items-center justify-center rounded-full text-xs font-bold transition-all', isSelected && 'bg-purple-600 text-white shadow-xs', isCurrentDay && !isSelected && 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100', !isSelected && !isCurrentDay && 'text-zinc-700 dark:text-zinc-300')}>
                                    {format(day, 'd')}
                                </span>
                            </button>;
        })}
                </div>
            </div>

            {/* Scrollable Timeline Grid */}
            <div ref={containerRef} className="relative flex flex-1 overflow-y-auto select-none" style={{
      scrollBehavior: 'smooth'
    }}>
                {/* Left Time Column (24 Hours) */}
                <div {...weekSlot?.timeColumn} className={cn('w-16 sm:w-20 shrink-0 border-r border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-850/50', weekSlot?.timeColumn?.className)}>
                    {HOURS.map(hour => {
          const timeDate = new Date();
          timeDate.setHours(hour, 0, 0, 0);
          const formattedTime = format(timeDate, 'h a');
          return <div key={hour} style={{
            height: `${SLOT_HEIGHT * 2}px`
          }} className="relative border-b border-zinc-100 dark:border-zinc-800/80 pr-2 text-right">
                                <span className="-translate-y-1/2 block text-xs font-medium text-zinc-400 dark:text-zinc-500 pt-1">{formattedTime}</span>
                            </div>;
        })}
                </div>

                {/* 7 Columns Timeline Tracks */}
                <div {...weekSlot?.grid} className={cn('grid flex-1 grid-cols-7 relative', weekSlot?.grid?.className)}>
                    {weekDays.map((day, dayIdx) => {
          const dayStart = startOfDay(day);
          const dayEnd = endOfDay(day);
          const isLastColumn = dayIdx === weekDays.length - 1;
          const isCurrentDay = isToday(day);

          // Events occurring on this specific day
          const dayEvents = parsedEvents.filter(ev => {
            return ev.startDate <= dayEnd && ev.endDate >= dayStart;
          });

          // Calculate overlapping columns
          const positionedEvents = dayEvents.map((ev, i) => {
            const startMinutes = Math.max(0, differenceInMinutes(ev.startDate, dayStart));
            const duration = Math.max(20, differenceInMinutes(ev.endDate, ev.startDate));
            const top = startMinutes / 30 * SLOT_HEIGHT;
            const height = Math.max(32, duration / 30 * SLOT_HEIGHT);

            // Check for overlaps with preceding events
            const overlapping = dayEvents.filter((other, oi) => {
              if (oi >= i) return false;
              return ev.startDate < other.endDate && ev.endDate > other.startDate;
            });
            const overlapIndex = overlapping.length;
            const totalOverlapping = dayEvents.filter(other => ev.startDate < other.endDate && ev.endDate > other.startDate).length;
            return {
              ...ev,
              top,
              height,
              overlapIndex,
              totalOverlapping: Math.max(1, totalOverlapping)
            };
          });
          return <div key={day.toISOString()} {...weekSlot?.dayColumn?.(day)} className={cn('relative h-full flex flex-col', !isLastColumn && 'border-r border-zinc-200 dark:border-zinc-800', weekSlot?.dayColumn?.(day)?.className)}>
                                {/* 24 Hours x 2 Half-hour slots */}
                                {HOURS.map(hour => <React.Fragment key={hour}>
                                        {/* Top half hour (00-30) */}
                                        <div style={{
                height: `${SLOT_HEIGHT}px`
              }} onClick={() => onAddEvent?.(day, hour)} className="group relative border-b border-dashed border-zinc-100 dark:border-zinc-800/60 hover:bg-zinc-50/80 dark:hover:bg-zinc-800/40 cursor-pointer transition-colors">
                                            <button type="button" className="absolute right-1 bottom-1 hidden group-hover:inline-flex size-5 items-center justify-center rounded bg-white dark:bg-zinc-800 text-zinc-400 hover:text-purple-600 shadow-2xs border border-zinc-200 dark:border-zinc-700" title={`Add event at ${hour}:00`}>
                                                <Plus className="size-3" />
                                            </button>
                                        </div>

                                        {/* Bottom half hour (30-60) */}
                                        <div style={{
                height: `${SLOT_HEIGHT}px`
              }} onClick={() => onAddEvent?.(day, hour)} className="group relative border-b border-zinc-200/80 dark:border-zinc-800 hover:bg-zinc-50/80 dark:hover:bg-zinc-800/40 cursor-pointer transition-colors">
                                            <button type="button" className="absolute right-1 bottom-1 hidden group-hover:inline-flex size-5 items-center justify-center rounded bg-white dark:bg-zinc-800 text-zinc-400 hover:text-purple-600 shadow-2xs border border-zinc-200 dark:border-zinc-700" title={`Add event at ${hour}:30`}>
                                                <Plus className="size-3" />
                                            </button>
                                        </div>
                                    </React.Fragment>)}

                                {/* Event Cards in Day Column */}
                                {positionedEvents.map(ev => {
              const color = ev.color || 'brand';
              const colorStyle = CALENDAR_COLOR_MAP[color] || CALENDAR_COLOR_MAP.brand;

              // Width and offset when overlapping
              const widthPercent = 100 / ev.totalOverlapping;
              const leftPercent = ev.overlapIndex * widthPercent;
              const cardSlot = weekSlot?.eventCard?.(ev);
              return <div key={ev.id} onClick={e => {
                e.stopPropagation();
                onEventClick?.(ev);
              }} style={{
                top: `${ev.top}px`,
                height: `${ev.height}px`,
                width: `calc(${widthPercent}% - 4px)`,
                left: `calc(${leftPercent}% + 2px)`,
                zIndex: 10 + ev.overlapIndex
              }} {...cardSlot} className={cn('absolute flex flex-col justify-start overflow-hidden rounded-lg p-2 text-xs transition-shadow hover:shadow-md cursor-pointer ring-1 ring-inset', colorStyle.root, cardSlot?.className)} title={`${ev.title} (${format(ev.startDate, 'h:mm a')} - ${format(ev.endDate, 'h:mm a')})`}>
                                            <div className="flex items-center justify-between gap-1">
                                                <span className={cn('truncate font-bold leading-tight', colorStyle.label)}>{ev.title}</span>
                                                {ev.dot && <span className={cn('size-1.5 rounded-full shrink-0', colorStyle.dot)} />}
                                            </div>

                                            <time className={cn('text-[10px] mt-0.5 opacity-85', colorStyle.time)}>
                                                {format(ev.startDate, 'h:mm a')} – {format(ev.endDate, 'h:mm a')}
                                            </time>
                                        </div>;
            })}

                                {/* Real-time Indicator Line on Today's Column */}
                                {isCurrentDay && <div style={{
              top: `${currentTimeTop}px`
            }} {...weekSlot?.currentTimeIndicator} className={cn('pointer-events-none absolute right-0 left-0 z-30 flex items-center -translate-y-1/2', weekSlot?.currentTimeIndicator?.className)}>
                                        <div className="size-2.5 rounded-full bg-red-500 shadow-xs -ml-1.5 ring-2 ring-white dark:ring-zinc-900" />
                                        <div className="h-0.5 flex-1 bg-red-500 shadow-2xs" />
                                    </div>}
                            </div>;
        })}
                </div>
            </div>
        </div>;
};